import React, { useState } from "react";
import GlowButton from "./GlowButton";

const plans = [
  {
    name: "base",
    tagline: "get the essentials",
    price: "$30",
    borderColor: "#A335B7",
    buttonBg: "#A335B7",
    popular: false,
    selectedGradient: "linear-gradient(90deg, #50145A 0%, #7321E9 100%)",
    features: [
      "free 30 day trial",
      "geo online presence (1)",
      "unlimited chat ordering and chat",
      "free ai ordering and support (1k msgs)",
      "digital menus or catalogs (1)",
    ],
  },
  {
    name: "premium",
    tagline: "everything in base just more...",
    price: "$150",
    borderColor: "#6E9236",
    buttonBg: "#6E9236",
    popular: true,
    selectedGradient: "linear-gradient(90deg, #2B300F 0%, #7221E7 100%)",
    features: [
      "free 30 day trial",
      "geo online presence (3)",
      "team members (20)",
      "free ai ordering and support (40k msgs)",
      "digital menus or catalogs (10)",
    ],
  },
  {
    name: "premium plus",
    tagline: "everything in premium just way more...",
    price: "$230",
    borderColor: "#F8B042",
    buttonBg: "#F8B042",
    popular: false,
    selectedGradient: "linear-gradient(90deg, #33230F 0%, #7321E7 100%)",
    features: [
      "free 30 day trial",
      "geo online presence (6)",
      "team members (60)",
      "free ai ordering and support (100k msgs)",
      "digital menus or catalogs (20)",
    ],
  },
  {
    name: "ultimate",
    tagline: "everything you need...",
    price: "$400",
    borderColor: "#3CAA8C",
    buttonBg: "#3CAA8C",
    popular: false,
    selectedGradient: "linear-gradient(90deg, #1F4217 0%, #7221E6 100%)",
    features: [
      "free 30 day trial",
      "geo online presence (10)",
      "team members (150)",
      "free ai ordering and support (200k msgs)",
      "digital menus or catalogs (20)",
    ],
  },
];

const CheckIcon = ({ color }) => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="none">
    <polyline points="4,10 8,14 16,6" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PricingSection = () => {
  const [selected, setSelected] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  React.useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  return (
    <section className="w-full bg-white text-black py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-16 font-lexend">
      <div className="max-w-[1260px] mx-auto">

        {/* Heading */}
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xl sm:text-4xl md:text-3xl lg:text-[50px] font-bold text-[#FCBA05] mb-4">
            serve more, spend less!
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-bold leading-tight text-black">
            simple transparent pricing
          </h2>
          <p className="text-gray-500 text-sm sm:text-base mt-3">
            start free → upgrade later. no commitment. cancel anytime.
          </p>
        </div>

        {/* Plan Rows */}
        <div className="flex flex-col gap-4 mb-12 max-w-[1100px] mx-auto px-6">
          {plans.map((plan) => {
            const isSelected = selected === plan.name;

            const textPrimary = isSelected ? "#ffffff" : "#111111";
            const textSecondary = isSelected ? "rgba(255,255,255,0.6)" : "#6b7280";
            const featureColor = isSelected ? "rgba(255,255,255,0.9)" : "#374151";
            const checkColor = isSelected ? "#ffffff" : plan.borderColor;

            const bgStyle = isSelected
              ? { backgroundImage: plan.selectedGradient }
              : { backgroundColor: "#ffffff" };

            const cardBorder = `1.5px solid ${plan.borderColor}`;

            return (
              <div
                key={plan.name}
                className="relative"
                style={{
                  paddingTop: plan.popular ? "14px" : "0",
                  marginLeft: isMobile ? "-12px" : (isSelected ? "-24px" : "0"),
                  marginRight: isMobile ? "-12px" : (isSelected ? "-24px" : "0"),
                  transition: "margin 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
              >
                {/* Most Popular Badge */}
                {plan.popular && (
                  <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" style={{ top: "14px" }}>
                    <span
                      className="text-xs font-semibold px-5 py-1.5 rounded-full text-white whitespace-nowrap"
                      style={{ backgroundColor: "#4CAF50" }}
                    >
                      most popular
                    </span>
                  </div>
                )}

                <div
                  onClick={() => setSelected(isSelected ? null : plan.name)}
                  className="relative flex flex-col md:flex-row md:items-center rounded-3xl overflow-hidden"
                  style={{
                    ...bgStyle,
                    border: cardBorder,
                    borderLeft: isMobile
                      ? cardBorder
                      : `${isSelected ? "16px" : "8px"} solid ${plan.borderColor}`,
                    borderBottom: isMobile ? `10px solid ${plan.borderColor}` : cardBorder,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transform: isSelected && !isMobile ? "translateY(-6px)" : "translateY(0)",
                    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    cursor: "pointer",
                  }}
                >
                  {/* Name + Tagline */}
                  <div className="px-6 pt-5 pb-2 md:py-4 md:w-[210px] md:shrink-0">
                    <p className="text-xl font-bold lowercase" style={{ color: textPrimary }}>
                      {plan.name}
                    </p>
                    <p className="text-sm mt-1 leading-snug" style={{ color: textSecondary }}>
                      {plan.tagline}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="flex-1 px-6 pb-3 md:py-4">
                    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm" style={{ color: featureColor }}>
                          <CheckIcon color={checkColor} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Price + Button */}
                  <div className="px-6 pb-5 pt-3 md:py-4 md:w-[210px] md:shrink-0 flex flex-col md:items-end gap-3">
                    <div className="flex flex-col gap-3 w-fit md:w-auto md:items-end">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold" style={{ color: textPrimary }}>
                        {plan.price}/
                      </span>
                      <span className="text-xs leading-tight whitespace-nowrap" style={{ color: textSecondary }}>
                        month (excl. vat)
                      </span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); window.location.href = "/business/get-started"; }}
                      className="w-full md:w-auto whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-semibold text-center transition-opacity duration-200 hover:opacity-90"
                      style={{
                        backgroundColor: isSelected ? "#ffffff" : plan.buttonBg,
                        color: isSelected ? "#6E3FC6" : "#ffffff",
                        border: "none",
                      }}
                    >
                      free trial
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTAs */}
        <div className="flex flex-wrap justify-center items-center gap-4">
          <button
            onClick={() => window.location.href = "/business/demo"}
            className="inline-flex items-center justify-center whitespace-nowrap border border-[#6E3FC6] bg-[#e9d9ff] hover:bg-[#d8bfff] text-black text-sm sm:text-base font-medium px-[18px] sm:px-[25px] lg:px-[40px] h-[48px] rounded-full transition-all duration-300 cursor-pointer"
          >
            how it works
          </button>
          <span>
            <GlowButton
              btnValue="free trial"
              btn1="2"
              onClick={() => window.location.href = "/business/get-started"}
            />
          </span>
        </div>

      </div>
    </section>
  );
};

export default PricingSection;
